import { useRef } from 'react'
import React from 'react'

import { ApolloError } from '@apollo/client'
import {
  Box,
  Dimensions,
  EnhancedDimension,
  InfiniteScroll,
} from '@factorialco/deprecated-design-system'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { Exact, PaginatedQueryPageInfoFragment } from 'generated/resources/graphql'

import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import type { UseQueryType } from 'modules/core/hooks/useQuery'

import { DefaultLoading } from './DefaultLoading'

interface Props<
  TResult,
  Node extends { [key: string]: unknown },
  Variables extends { [key: string]: unknown },
  Edge extends { cursor: string; node: Node },
> {
  document: TypedDocumentNode<TResult, Variables>
  variables: Omit<Exact<Variables>, 'search' | 'after'>
  extractConnection: (data: NonNullable<UseQueryType<TResult, Variables>['data']>) => {
    pageInfo: PaginatedQueryPageInfoFragment
    edges: Array<Edge>
  }
  height: EnhancedDimension
  width?: EnhancedDimension
  minWidth?: EnhancedDimension
  maxWidth?: EnhancedDimension
  firstContent?: () => JSX.Element
  content: (node: Node) => JSX.Element
  noContent?: () => JSX.Element
  errorContent?: (error: ApolloError) => JSX.Element
  loadingContent?: () => JSX.Element
  flexDirection?: 'row' | 'column'
  gap?: Dimensions
  filterEntities?: (entities: Edge['node'][]) => Edge['node'][]
}

/**
 * This component manage queries using a custom implementation of Apollo's `useQuery` hook, tailored for use within Factorial. It adds
 * default behavior that is opinionated towards Factorial's needs. This includes additional loading states that provide
 * more detailed information about the query's progress, and also improved error handling.
 *
 * @template Query The GraphQL query type.
 * @template Variables The variables for the GraphQL query, must be an object with string keys and unknown values.
 *
 * @param {TypedDocumentNode<Query, Variables>} document The GraphQL query document.
 * @param {options} options The query hook options, may include variables for the query and extra params used to handle errors.
 *
 * @returns {JSX.Element} The result of rendering the content function with the query data.
 */
export const PaginatedQueryContainer = function PaginatedQueryContainer<
  TResults,
  Node extends { [key: string]: unknown },
  Variables extends { [key: string]: unknown },
  Edge extends { cursor: string; node: Node },
>({
  document,
  firstContent,
  content,
  noContent,
  loadingContent,
  errorContent,
  variables,
  extractConnection,
  height,
  width,
  minWidth,
  maxWidth,
  gap,
  flexDirection,
  filterEntities,
}: Props<TResults, Node, Variables, Edge>) {
  const {
    error,
    loading,
    controls: { fetchMore, hasMore },
    nodes,
  } = usePaginatedQuery<TResults, Variables, Edge>(document, {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    variables: {
      ...variables,
    } as Omit<Variables, 'after'>,
    extractConnection,
  })
  const htmlRef = useRef<HTMLDivElement>(null)

  if (loading) {
    return loadingContent ? (
      <Box
        maxHeight={height}
        position="relative"
        overflowY="auto"
        overflowX="hidden"
        width={width}
        minWidth={minWidth}
        maxWidth={maxWidth}
        ref={htmlRef}
      >
        {loadingContent()}
      </Box>
    ) : (
      <DefaultLoading width={width} minWidth={minWidth} maxWidth={maxWidth} key="loading" />
    )
  }
  if (error) {
    return errorContent ? errorContent(error) : <></>
  }
  if (!nodes || !nodes.length) {
    return noContent ? noContent() : firstContent ? firstContent() : <></>
  }

  const filterdNodes = filterEntities ? filterEntities(nodes) : nodes

  return (
    <Box
      data-scroll-container
      maxHeight={height}
      position="relative"
      overflowY="auto"
      overflowX="hidden"
      width={width}
      minWidth={minWidth}
      maxWidth={maxWidth}
      ref={htmlRef}
    >
      <InfiniteScroll
        threshold={48}
        loadMore={fetchMore}
        hasMore={hasMore}
        loader={
          <DefaultLoading width={width} minWidth={minWidth} maxWidth={maxWidth} key="loading" />
        }
      >
        <Box flexDirection={flexDirection ?? 'column'} gap={gap} flexWrap="wrap" tabIndex={0}>
          {!!firstContent && firstContent()}

          {filterdNodes.map((node: Edge['node'], index: number) => {
            // Use node.id if available, otherwise fall back to index
            const key = 'id' in node && node.id ? String(node.id) : index
            return <React.Fragment key={key}>{content(node)}</React.Fragment>
          })}
        </Box>
      </InfiniteScroll>
    </Box>
  )
}
